import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Dialog, Transition } from "@headlessui/react";
import {
  differenceInCalendarDays,
  endOfDay,
  format,
  startOfDay,
  subDays,
} from "date-fns";
import { CalendarDays } from "lucide-react";
import { useTranslation } from "react-i18next";

import { XIcon } from "../../components/icons";
import { getDateFnsLocale } from "../../utils/getDateFnsLocale";
import DateRangeCalendar from "../DateRangeCalendar/DateRangeCalendar";
import SmallCFLoader from "../SmallCFLoader/SmallCFLoader";

export const MAX_EXPORT_RANGE_DAYS = 180;

const RangeSummaryField = ({ label, value, placeholder, isActive, testId }) => (
  <div className="min-w-0 flex-1">
    <span className="mb-1.5 block text-xs font-medium text-gray-700">
      {label}
    </span>
    <div
      id={testId}
      className={`flex h-10 items-center rounded-lg border px-3 text-sm transition-colors ${
        isActive
          ? "border-cfPrimaryButton bg-cfTemplateTransitionColor2 text-cfPrimary"
          : "border-gray-300 bg-white"
      } ${value ? "text-gray-900" : "text-gray-400"}`}
    >
      <span className="truncate">{value || placeholder}</span>
    </div>
  </div>
);

const ExportDateRangeModal = ({
  isOpen,
  onClose,
  onConfirm,
  isExporting = false,
  maxRangeDays = MAX_EXPORT_RANGE_DAYS,
  testId = "export-date-range-modal",
}) => {
  const { t, i18n } = useTranslation();

  const [range, setRange] = useState({ startDate: null, endDate: null });
  const [today, setToday] = useState(() => startOfDay(new Date()));

  const { startDate, endDate } = range;
  const dateLocale = useMemo(
    () => getDateFnsLocale(i18n.language),
    [i18n.language],
  );

  const minSelectableDate = useMemo(
    () => subDays(today, maxRangeDays - 1),
    [today, maxRangeDays],
  );

  useEffect(() => {
    if (!isOpen) return;
    setRange({ startDate: null, endDate: null });
    setToday((current) => {
      const now = startOfDay(new Date());
      return current.getTime() === now.getTime() ? current : now;
    });
  }, [isOpen]);

  const handleClear = useCallback(
    () => setRange({ startDate: null, endDate: null }),
    [],
  );

  const isRangeValid = !!startDate && !!endDate && endDate >= startDate;
  const hasSelection = !!startDate || !!endDate;

  const formatSummary = (date) =>
    date ? format(date, "d MMM yyyy", { locale: dateLocale }) : "";

  const handleConfirm = () => {
    if (!isRangeValid || isExporting) return;
    onConfirm({
      start_date: startOfDay(startDate).toISOString(),
      end_date: endOfDay(endDate).toISOString(),
    });
  };

  const handleClose = () => {
    if (isExporting) return;
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={handleClose}
        aria-labelledby={`${testId}-title`}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            id={`${testId}-backdrop`}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm"
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto px-4 py-6">
          <div className="flex min-h-full items-center justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id={testId}
                className="w-full max-w-[41rem] transform rounded-2xl bg-white text-left align-middle shadow-xl transition-all"
              >
                <div className="flex items-start gap-4 p-6 pb-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-cfTemplateTransitionColor2">
                    <CalendarDays className="h-5 w-5 text-cfPrimary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-gray-900"
                      id={`${testId}-title`}
                    >
                      {t("finance:export_range_modal_title")}
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">
                      {t("finance:export_range_modal_subtitle", {
                        days: maxRangeDays,
                      })}
                    </p>
                  </div>
                  <button
                    type="button"
                    id={`${testId}-close`}
                    onClick={handleClose}
                    disabled={isExporting}
                    aria-label={t("workspace:close")}
                    className="-mr-1 -mt-1 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <XIcon size={20} className="inline-flex" aria-hidden />
                  </button>
                </div>

                <div className="flex flex-wrap items-end gap-x-3 gap-y-2 px-6">
                  <div className="flex min-w-0 flex-1 items-end gap-3">
                    <RangeSummaryField
                      testId={`${testId}-start-value`}
                      label={t("verificationApprovals:start_date")}
                      value={formatSummary(startDate)}
                      placeholder={t("verificationApprovals:start_date")}
                      isActive={!startDate}
                    />
                    <span aria-hidden="true" className="pb-2.5 text-gray-300">
                      —
                    </span>
                    <RangeSummaryField
                      testId={`${testId}-end-value`}
                      label={t("verificationApprovals:end_date")}
                      value={formatSummary(endDate)}
                      placeholder={t("verificationApprovals:end_date")}
                      isActive={!!startDate && !endDate}
                    />
                  </div>
                  <button
                    type="button"
                    id={`${testId}-clear`}
                    onClick={handleClear}
                    disabled={!hasSelection || isExporting}
                    className="h-10 w-full shrink-0 rounded-lg px-3 text-right text-sm font-medium text-cfPrimary transition-colors hover:bg-cfTemplateTransitionColor2 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent sm:w-auto sm:text-center"
                  >
                    {t("finance:export_range_clear")}
                  </button>
                </div>

                <div className="mt-5 border-t border-gray-100 px-6 pt-4">
                  <DateRangeCalendar
                    testId={`${testId}-calendar`}
                    startDate={startDate}
                    endDate={endDate}
                    onChange={setRange}
                    minDate={minSelectableDate}
                    maxDate={today}
                    maxRangeDays={maxRangeDays}
                    disabled={isExporting}
                  />
                </div>

                <div className="flex flex-col gap-3 rounded-b-2xl border-t border-gray-100 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <p
                    className="text-xs text-gray-500 sm:flex-1"
                    id={`${testId}-hint`}
                    aria-live="polite"
                  >
                    {isRangeValid
                      ? t("finance:export_range_days_selected", {
                          count:
                            differenceInCalendarDays(endDate, startDate) + 1,
                        })
                      : `${t("verificationApprovals:select_start_date_between")} ${format(
                          minSelectableDate,
                          "MMM d, yyyy",
                          { locale: dateLocale },
                        )} ${t("verificationApprovals:and")} ${format(
                          today,
                          "MMM d, yyyy",
                          { locale: dateLocale },
                        )}`}
                  </p>
                  <div className="flex shrink-0 flex-col-reverse gap-3 sm:flex-row sm:items-center">
                    <button
                      type="button"
                      id={`${testId}-cancel`}
                      onClick={handleClose}
                      disabled={isExporting}
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t("verificationApprovals:cancel")}
                    </button>
                    <button
                      type="button"
                      id={`${testId}-confirm`}
                      onClick={handleConfirm}
                      disabled={!isRangeValid || isExporting}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-cfPrimaryButton px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-cfPrimaryDark focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-cfDisabled"
                    >
                      {isExporting ? (
                        <SmallCFLoader text={t("finance:exporting")} />
                      ) : (
                        t("finance:download_excel")
                      )}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ExportDateRangeModal;
